/* Copyright (c) 2020-present CloudIO Inc */

import { pageAtom } from 'designer/atoms';
import {
  AnalyzeContext,
  AnalyzeIssue,
  AnalyzeResult,
} from 'layout/Sidebar/panels/TreeView/AnalyzeUtils';
import { itemWithDefaultValues } from 'registry/Components';
import { Item, RequiredItem } from '../../registry/Component.type';

export async function analyzeNumberField({
  platform,
  appUid,
  pageId,
  itemId,
  mgr,
}: AnalyzeContext): Promise<AnalyzeResult> {
  const alerts: AnalyzeIssue[] = [];
  const autoFixes: (() => void)[] = [];
  const page = platform.getPage(appUid, pageId);
  if (!page) {
    return null;
  }
  const store = platform.getParentStore(appUid, pageId, itemId);
  const item = itemWithDefaultValues(
    page[itemId],
  ) as RequiredItem<'NumberField'>;
  const {
    viewAttribute,
    label,
    required,
    allowDecimal,
    insertable,
    updatable,
  } = item.props;

  if (!store) {
    alerts.push({
      key: `incomplete-${itemId}`,
      showItemId: itemId,
      alert: `Incomplete ${mgr.name}. Must be wrapped inside a Data Store Reference!`,
      itemId,
    });
    return [alerts, autoFixes];
  }
  if (!viewAttribute) {
    return [alerts, autoFixes];
  }
  await store.initializeAttributes();

  const attr = store.getAttribute(viewAttribute);

  if (!attr) {
    return [alerts, autoFixes];
  }

  if (attr.attributeType === 'Integer' && allowDecimal) {
    const fix = () => {
      const _page = { ...platform.getPage(appUid, pageId) };
      const _item = _page[itemId] as Item<'NumberField'>;
      const updatedItem: Item<'NumberField'> = {
        ..._item,
        props: { ..._item.props, allowDecimal: false },
      };
      _page[_item.itemId] = updatedItem;
      platform.setter(pageAtom, _page, {
        params: { appUid, pageId },
      });
    };
    autoFixes.push(fix);
    alerts.push({
      key: `allowDecimal-${itemId}`,
      fix,
      showItemId: itemId,
      alert: `${mgr.name} [${label}]'s Allow Decimal property is enabled for an integer attribute [${attr.attributeName}] in data source ${store.ds}!`,
      itemId,
      severity: 'warning',
    });
  }
  let readonly = store.isReadonly();
  if (!readonly) {
    readonly =
      insertable.type === 'boolean' &&
      !insertable.value &&
      updatable.type === 'boolean' &&
      !updatable.value;
  }
  if (!readonly && attr.allowBlank === 'N' && !required) {
    const fix = () => {
      const _page = { ...platform.getPage(appUid, pageId) };
      const _item = _page[itemId] as Item<'NumberField'>;
      const updatedItem: Item<'NumberField'> = {
        ..._item,
        props: { ..._item.props, required: true },
      };
      _page[_item.itemId] = updatedItem;
      platform.setter(pageAtom, _page, {
        params: { appUid, pageId },
      });
    };
    autoFixes.push(fix);
    alerts.push({
      key: `allowBlank-${itemId}`,
      fix,
      showItemId: itemId,
      alert: `${mgr.name} [${label}]'s is not marked as required, but the Attribute [${attr.attributeName}] in ${store.ds} is not optional!`,
      itemId,
      severity: 'warning',
    });
  }

  return [alerts, autoFixes];
}
